import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useAnalytics } from "~/components/Analytics";
import { Divider } from "~/components/Divider";
import { PageMeta } from "~/components/PageMeta";
import { usePiano } from "~/components/Piano/PianoContext";
import { SubCheckoutBillingAndPayment } from "~/components/SubCheckoutV2/SubCheckoutBillingAndPayment";
import {
  SubCheckoutBreadcrumbs,
  SubCheckoutBreadcrumbsProps,
} from "~/components/SubCheckoutV2/SubCheckoutBreadcrumbs";
import { SubCheckoutCompletion } from "~/components/SubCheckoutV2/SubCheckoutCompletion";
import { SubCheckoutHeader } from "~/components/SubCheckoutV2/SubCheckoutHeader";
import {
  CreateYourAccountStep,
  SubCheckoutPageCreateYourAccount,
} from "~/components/SubCheckoutV2/SubCheckoutPage/SubCheckoutPageCreateYourAccount";
import { usePostCheckoutHandler } from "~/components/SubCheckoutV2/SubCheckoutPage/usePostCheckoutHandler";
import { SubCheckoutSelectedSubscription } from "~/components/SubCheckoutV2/SubCheckoutSelectedSubscription";
import { SubscriptionTrialInlineWarning } from "~/components/SubscriptionTrialInlineWarning";
import { UserOnboardingModal } from "~/components/UserOnboardingForm/UserOnboardingModal";
import { getValueOrDefault } from "~/lib/shared/getValueOrDefault";
import { subscriptionMapV3 } from "~/lib/shared/subscriptionsV3";
import { SubscriptionName } from "~/types/endil/subscription";

export enum SubCheckoutV2Step {
  ACCOUNT = 0,
  BILLING = 1,
  COMPLETE = 2,
}

export type SubCheckoutPageV2Props = {
  defaultAccountStep?: CreateYourAccountStep;
  defaultSelectedSubscription: SubscriptionName;
  defaultStep: SubCheckoutV2Step;
  showTrialWarning: boolean;
  /**
   * Enables a useEffect which will auto correct the current step based on the user's status.
   * Defaults to true.
   *
   * This is only necessary to allow for more predictable tests.
   * */
  autoUpdateUserState?: boolean;
};

/** exported only for testing */
export const getAccountStepStatus = (
  activeCheckoutStep: SubCheckoutV2Step,
  activeAccountCreationStep: CreateYourAccountStep
): SubCheckoutBreadcrumbsProps["breadcrumbs"][number]["status"] => {
  if (activeCheckoutStep > SubCheckoutV2Step.ACCOUNT) {
    return "complete";
  }
  if (activeAccountCreationStep === CreateYourAccountStep.VERIFY) {
    return "info";
  }
  return "active";
};

/** exported only for testing */
export const getBillingStepStatus = (activeStep: SubCheckoutV2Step) => {
  if (activeStep < SubCheckoutV2Step.BILLING) {
    return "pending";
  }

  if (activeStep === SubCheckoutV2Step.BILLING) {
    return "active";
  }

  return "complete";
};

/** exported only for testing */
export const getCompleteStepStatus = (activeStep: SubCheckoutV2Step) => {
  if (activeStep < SubCheckoutV2Step.COMPLETE) {
    return "pending";
  }

  return "complete";
};

export const SubCheckoutPage: React.FC<SubCheckoutPageV2Props> = ({
  autoUpdateUserState,
  defaultAccountStep,
  defaultStep,
  defaultSelectedSubscription,
  showTrialWarning,
}) => {
  const { analyticsEnhancedTrack } = useAnalytics();

  const [subscriptionData, setSubscriptionData] = useState(
    subscriptionMapV3.get(defaultSelectedSubscription) ??
      subscriptionMapV3.get(SubscriptionName.ONE_SEAT_ANNUAL)!
  );

  const [activeAccountStep, setActiveAccountStep] =
    useState<CreateYourAccountStep>(
      getValueOrDefault(defaultAccountStep, CreateYourAccountStep.ENTRY)
    );
  const [activeStep, setActiveStep] = useState<SubCheckoutV2Step>(defaultStep);
  const [thrownError, setThrownError] = useState<Error | null>(null);
  const [, setIsProcessingSubscription] = useState(false);

  const { data: session, status } = useSession();
  const mcAccountId = session?.mcAccountId;
  const { piano } = usePiano();
  const { replace, asPath } = useRouter();

  const {
    pianoOffer,
    subCheckoutHeaderProps,
    subCheckoutSelectedSubscriptionProps,
  } = subscriptionData;

  const onPostCheckout = usePostCheckoutHandler({
    mcAccountId,
    pianoOffer,
  });

  const pianoCompleteHandler = async () => {
    setIsProcessingSubscription(true);
    await onPostCheckout().catch((err) => {
      setThrownError(err);
    });
    setIsProcessingSubscription(false);
  };

  const onCheckout = async () => {
    analyticsEnhancedTrack("Subscription Checkout Started", {
      subscription: {
        ...pianoOffer,
      },
    });
    piano?.offer.startCheckout({
      ...pianoOffer,
      complete: pianoCompleteHandler,
    });
  };

  // this is completely optional testing prop - default value to true
  const allowAutoUpdate = getValueOrDefault(autoUpdateUserState, true);

  useEffect(() => {
    if (!allowAutoUpdate || status === "loading") {
      return;
    }

    if (session?.user.isActiveSubscriber) {
      setActiveStep(SubCheckoutV2Step.COMPLETE);
      return;
    }

    if (status === "authenticated") {
      setActiveStep(SubCheckoutV2Step.BILLING);
      return;
    }

    setActiveStep(SubCheckoutV2Step.ACCOUNT);
  }, [allowAutoUpdate, session?.user.isActiveSubscriber, status]);

  if (thrownError) {
    throw thrownError;
  }

  return (
    <>
      <PageMeta
        seo={{
          nofollow: true,
          noindex: true,
          title: "Subscription Checkout",
        }}
      />
      <div className="flex size-full grow flex-col md:flex-row">
        <div className="grow p-m md:pb-xxl">
          <div className="mx-auto max-w-[640px]">
            <SubCheckoutHeader
              showImage={
                activeStep === SubCheckoutV2Step.COMPLETE
                  ? false
                  : getValueOrDefault(subCheckoutHeaderProps.showImage, true)
              }
              headline={
                activeStep === SubCheckoutV2Step.COMPLETE
                  ? "Checkout Complete"
                  : subCheckoutHeaderProps.headline
              }
              bodyText={
                activeStep === SubCheckoutV2Step.COMPLETE
                  ? null
                  : subCheckoutHeaderProps.bodyText
              }
            />
          </div>
          <Divider containerClassName="my-l" inset="m" />
          <div className="mx-auto mb-m max-w-[640px]">
            <SubCheckoutBreadcrumbs
              breadcrumbs={[
                {
                  status: getAccountStepStatus(activeStep, activeAccountStep),
                  title: "Account",
                },
                {
                  status: getBillingStepStatus(activeStep),
                  title: "Billing & Payment",
                },
                {
                  status: getCompleteStepStatus(activeStep),
                  title: "Complete",
                },
              ]}
            />
          </div>
          {showTrialWarning && activeStep !== SubCheckoutV2Step.COMPLETE ? (
            <div className="mx-auto mb-m max-w-[750px]">
              <SubscriptionTrialInlineWarning />
            </div>
          ) : (
            <div className="mx-auto w-full md:max-w-[640px]">
              {activeStep === SubCheckoutV2Step.ACCOUNT ? (
                <SubCheckoutPageCreateYourAccount
                  activeStep={activeAccountStep}
                  setActiveStep={setActiveAccountStep}
                  postLoginRedirect={asPath}
                />
              ) : null}
              {activeStep === SubCheckoutV2Step.BILLING && session ? (
                <SubCheckoutBillingAndPayment
                  disableAddPayment={showTrialWarning}
                  email={getValueOrDefault(session.user.email, "")}
                  firstName={getValueOrDefault(session.user.firstName, "")}
                  lastName={getValueOrDefault(session.user.lastName, "")}
                  onAddButtonClick={onCheckout}
                />
              ) : null}
              {activeStep === SubCheckoutV2Step.COMPLETE ? (
                <SubCheckoutCompletion />
              ) : null}
            </div>
          )}
        </div>

        {showTrialWarning ||
        activeStep === SubCheckoutV2Step.COMPLETE ? null : (
          <div
            className="w-full border-gray-300 bg-gray-100 p-m md:max-w-[390px] md:border-l-1 md:p-0"
            data-testid="checkout-sidebar"
          >
            <SubCheckoutSelectedSubscription
              {...subCheckoutSelectedSubscriptionProps}
              onPlanChange={(newPlanName) => {
                setSubscriptionData(subscriptionMapV3.get(newPlanName)!);
                replace(`/subscription-checkout?subscription=${newPlanName}`);
              }}
            />
          </div>
        )}
      </div>
      <UserOnboardingModal isOpen={activeStep === SubCheckoutV2Step.COMPLETE} />
    </>
  );
};
